import React from 'react';
import {
  Box,
  Collapse,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SyncIcon from '@mui/icons-material/Sync';
import {
  FileJobStatus,
  TransferJob,
  TransferJobStatus,
  useTransferManagerContext,
} from '../../../context/TransferManagerContext';

const MAX_VISIBLE_FILES = 10;

const TYPE_ICON: Record<TransferJob['type'], React.ReactNode> = {
  download: <DownloadIcon sx={{ fontSize: 16 }} />,
  sync: <SyncIcon sx={{ fontSize: 16 }} />,
  create: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />,
};

const getJobStatusMessage = (job: TransferJob): string | null => {
  if (job.statusMessage) return job.statusMessage;
  if (job.status === TransferJobStatus.QUEUED) return 'Waiting in queue';
  if (job.status !== TransferJobStatus.ACTIVE) return null;

  switch (job.type) {
    case 'create':
      return 'Creating Version';
    case 'sync':
      return 'Creating Version';
    case 'download':
      return 'Downloading';
    default:
      return null;
  }
};

// ─── Single job card ──────────────────────────────────────────────────────────

const JobCard: React.FC<{
  job: TransferJob;
  onCancel: () => void;
}> = ({ job, onCancel }) => {
  const overallProgress =
    job.totalBytes > 0
      ? Math.round((job.completedBytes / job.totalBytes) * 100)
      : 0;

  const activeFiles = job.fileJobs
    .filter((fj) => fj.status === FileJobStatus.ACTIVE)
    .slice(0, MAX_VISIBLE_FILES);
  const hasTransferStarted = job.fileJobs.some(
    (fj) => fj.status !== FileJobStatus.PENDING
  );
  const statusMessage = getJobStatusMessage(job);
  const showStatusSpinner =
    !!statusMessage &&
    (job.status === TransferJobStatus.QUEUED ||
      job.status === TransferJobStatus.ACTIVE);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        '&:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {/* Header: type icon + project name + cancel */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
          {TYPE_ICON[job.type]}
        </Box>
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {job.projectName}
        </Typography>
        <Tooltip title="Cancel">
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {statusMessage && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}
        >
          <Typography variant="caption" color="text.secondary">
            {statusMessage}
          </Typography>

          {showStatusSpinner && (
            <CircularProgress
              size={12}
              thickness={7}
              sx={{ color: 'success.light' }}
            />
          )}
        </Box>
      )}

      {/* Progress section appears only once bytes actually start transferring */}
      <Collapse in={hasTransferStarted} timeout={260} unmountOnExit>
        <Box sx={{ mb: activeFiles.length > 0 ? 1.5 : 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {job.completedFiles}&thinsp;/&thinsp;{job.totalFiles} files
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {overallProgress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        {/* Per-file rows (only files currently in-flight) */}
        {activeFiles.map((fj) => (
          <Box key={fj.fileName} sx={{ mb: 0.75 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                mb: 0.25,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mr: 1,
                }}
              >
                {fj.fileName}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ flexShrink: 0 }}
              >
                {fj.progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={fj.progress}
              sx={{ height: 3, borderRadius: 1.5 }}
            />
          </Box>
        ))}
      </Collapse>
    </Box>
  );
};

// ─── Empty state ─────────────────────────────────────────────────────────────

const EmptyState: React.FC = () => (
  <Box sx={{ px: 2, pb: 2 }}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        py: 2.5,
        borderRadius: '8px',
        bgcolor: 'studio.surfaces.fillWeak',
      }}
    >
      <Box sx={{ color: 'text.disabled', display: 'flex' }}>
        <DownloadIcon fontSize="small" />
      </Box>
      <Typography variant="caption" color="text.disabled" align="center">
        No active transfers
      </Typography>
    </Box>
  </Box>
);

// ─── Panel (section header + content) ────────────────────────────────────────

export const TransferQueuePanel: React.FC = () => {
  const { queue, cancelJob } = useTransferManagerContext();

  const activeJobs = queue.filter(
    (j) =>
      j.status === TransferJobStatus.ACTIVE ||
      j.status === TransferJobStatus.QUEUED
  );
  const waitingCount = queue.filter(
    (j) => j.status === TransferJobStatus.QUEUED
  ).length;

  return (
    <Box>
      {/* Section header */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="overline" color="text.secondary">
          Queue
        </Typography>
        {waitingCount > 0 && (
          <Chip
            label={`${waitingCount} waiting`}
            size="small"
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        )}
      </Box>

      {/* Jobs or empty placeholder */}
      {activeJobs.length === 0 ? (
        <EmptyState />
      ) : (
        activeJobs.map((job) => (
          <JobCard key={job.id} job={job} onCancel={() => cancelJob(job.id)} />
        ))
      )}
    </Box>
  );
};
